import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

// Helper to normalize apostrophes and trim whitespace in role strings
function normalizeRole(role) {
  return role.replace(/[’‘]/g, "'").trim();
}

/**
 * RoleGuard component to protect routes based on user roles
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if user has access
 * @param {string[]} props.allowedRoles - Array of roles that can access this route
 * @param {string} props.redirectTo - Path to redirect to if access is denied (default: '/Home')
 */
const RoleGuard = ({ children, allowedRoles, redirectTo = '/Home' }) => {
  const [hasAccess, setHasAccess] = useState(null);
  
  useEffect(() => {
    const checkAccess = () => {
      // Get user details from localStorage
      const userDetailsString = localStorage.getItem('user-details');
      
      if (!userDetailsString) {
        // No user details, redirect to login
        setHasAccess(false);
        return;
      }
      
      try {
        const userDetails = JSON.parse(userDetailsString);
        const userRoles = userDetails?.data?.roles || [];
        // Normalize roles for robust comparison
        const normalizedUserRoles = userRoles.map(normalizeRole);
        const normalizedAllowedRoles = allowedRoles.map(normalizeRole);
        // Check if user has any of the allowed roles
        const hasAllowedRole = normalizedAllowedRoles.some(role => {
          if (Array.isArray(role)) {
            return role.some(subRole => normalizedUserRoles.includes(normalizeRole(subRole)));
          }
          return normalizedUserRoles.includes(role);
        });
        setHasAccess(hasAllowedRole);
      } catch (error) {
        console.error('Error checking user roles:', error);
        setHasAccess(false);
      }
    };
    
    checkAccess();
  }, [allowedRoles]);
  
  // Show nothing while checking access
  if (hasAccess === null) {
    return null;
  }
  
  // Redirect if no access
  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }
  
  // Render children if has access
  return children;
};

export default RoleGuard; 